import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LAUNCH_DATE, api, gardenCount } from "@/lib/api";

const computeRemaining = (target) => {
    const now = new Date();
    let ms = target - now;
    if (ms < 0) ms = 0;

    let months = (target.getFullYear() - now.getFullYear()) * 12 + (target.getMonth() - now.getMonth());
    let anchor = new Date(now);
    anchor.setMonth(anchor.getMonth() + months);
    if (anchor > target) {
        months -= 1;
        anchor = new Date(now);
        anchor.setMonth(anchor.getMonth() + months);
    }
    const rem = Math.max(0, target - anchor);
    const days = Math.floor(rem / 86400000);
    const hours = Math.floor((rem % 86400000) / 3600000);
    const minutes = Math.floor((rem % 3600000) / 60000);
    const seconds = Math.floor((rem % 60000) / 1000);
    return { months, days, hours, minutes, seconds };
};

const UNITS = [
    ["months", "Months"],
    ["days", "Days"],
    ["hours", "Hours"],
    ["minutes", "Minutes"],
    ["seconds", "Seconds"],
];

export default function CountdownTimer() {
    const [target, setTarget] = useState(LAUNCH_DATE);
    const [t, setT] = useState(() => computeRemaining(LAUNCH_DATE));
    const [real, setReal] = useState(0);

    useEffect(() => {
        const fetchDate = () =>
            api.get("/settings/launch-date")
                .then(({ data }) => {
                    if (data.launch_date) setTarget(new Date(data.launch_date));
                })
                .catch(() => {});
        fetchDate();
        window.addEventListener("launch-date-changed", fetchDate);
        api.get("/signups/count")
            .then(({ data }) => setReal(data.count))
            .catch(() => {});
        return () => window.removeEventListener("launch-date-changed", fetchDate);
    }, []);

    useEffect(() => {
        setT(computeRemaining(target));
        const id = setInterval(() => setT(computeRemaining(target)), 1000);
        return () => clearInterval(id);
    }, [target]);

    return (
        <section data-testid="countdown-section" className="relative mx-auto max-w-6xl px-6 py-32">
            <div className="grid items-center gap-16 lg:grid-cols-2">
                <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                >
                    <img
                        src="/assets/promo-card.png"
                        alt="Eve Came First — A Myth Reimagined, Coming Soon"
                        data-testid="promo-card-image"
                        className="book-glow w-full rounded-md"
                    />
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
                    className="text-center lg:text-left"
                >
                    <p className="font-cinzel text-xs tracking-[0.4em] text-[#b89860] uppercase">
                        A Myth Reimagined — Coming Soon
                    </p>
                    <h2 className="font-cinzel mt-6 text-3xl leading-snug text-[#f3e5ab] sm:text-5xl">
                        The Unveiling
                    </h2>
                    <p className="font-cormorant mt-4 text-lg italic text-[#a3b899]">
                        The anticipated arrival — approximately four months from now.
                    </p>

                    <div className="mt-12 flex flex-wrap justify-center gap-3 lg:justify-start sm:gap-4">
                        {UNITS.map(([key, label]) => (
                            <div
                                key={key}
                                data-testid={`countdown-${key}`}
                                className="min-w-[76px] rounded-sm border border-[rgba(212,175,55,0.3)] bg-[rgba(15,35,24,0.55)] px-3 py-4 backdrop-blur-md sm:min-w-[92px] sm:px-5 sm:py-6"
                            >
                                <div className="font-cinzel text-3xl text-[#f3e5ab] tabular-nums sm:text-4xl">
                                    {String(t[key]).padStart(2, "0")}
                                </div>
                                <div className="font-cinzel mt-2 text-[0.6rem] tracking-[0.3em] text-[#b89860] uppercase">
                                    {label}
                                </div>
                            </div>
                        ))}
                    </div>

                    <p data-testid="countdown-souls" className="font-cormorant mt-8 text-base italic text-[#c8b090]">
                        {gardenCount(real)} souls are already waiting for its arrival.
                    </p>
                </motion.div>
            </div>
        </section>
    );
}
